import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, MapPinIcon } from 'lucide-react';
const faqs = [{
  question: 'Are the procedures safe?',
  answer: "Absolutely. All our procedures are performed by board-certified surgeons and medical professionals in state-of-the-art facilities. We follow strict FDA guidelines and use only approved equipment and techniques. Your safety is our top priority, and we conduct thorough consultations to ensure you're a good candidate for any procedure."
}, {
  question: 'What is the recovery time?',
  answer: "Recovery time varies depending on the procedure. Non-invasive treatments like laser lipo may require just 1-2 days of rest, while surgical procedures like tummy tucks or BBL typically require 2-4 weeks of recovery. During your consultation, we'll provide a detailed recovery timeline specific to your treatment plan."
}, {
  question: 'Do you offer financing options?',
  answer: 'Yes! We understand that aesthetic treatments are an investment in yourself. We offer flexible financing options including payment plans, and we work with several financing partners to help make your transformation affordable. Many patients qualify for 0% interest financing for qualified applicants.'
}, {
  question: 'How much pain should I expect?',
  answer: "We prioritize your comfort throughout the entire process. Most procedures are performed under anesthesia, so you won't feel anything during the treatment. Post-procedure discomfort is typically manageable with prescribed pain medication. Our team will provide detailed aftercare instructions to minimize any discomfort during recovery."
}, {
  question: 'How soon will I see results?',
  answer: "Results vary by procedure. Some treatments like skin rejuvenation show immediate improvements, while surgical procedures may take 3-6 months to see final results as swelling subsides. During your consultation, we'll set realistic expectations and provide a timeline for when you can expect to see your transformation."
}, {
  question: 'Can I combine multiple procedures?',
  answer: "Yes, many patients opt for combination procedures like the Mommy Makeover, which combines multiple treatments in one session. This can reduce overall recovery time and cost. During your consultation, we'll discuss which combinations are safe and effective for your goals."
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="w-full py-16 lg:py-20 bg-gradient-to-br from-brand-navy via-brand-navy to-brand-deep relative overflow-hidden" data-id="element-41">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-42">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-43" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-brand-aqua/10 rounded-full blur-3xl" data-id="element-44" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-45">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-46">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-47">
            Frequently Asked{' '}
            <span className="text-brand-primary" data-id="element-48">Questions</span>
          </h2>
          <p className="text-base text-white/70 max-w-2xl mx-auto" data-id="element-49">
            Get answers to common questions about our procedures and services
          </p>
        </motion.div>

        {/* FAQ accordion */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="space-y-3 mb-16" data-id="element-50">
          {faqs.map((faq, index) => <div key={index} className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden shadow-xl" data-id="element-51">
              <button onClick={() => toggleFAQ(index)} className="w-full flex items-center justify-between p-5 text-left transition-colors duration-300 hover:bg-white/5" aria-expanded={openIndex === index} aria-controls={`faq-answer-${index}`} data-id="element-52">
                <span className="text-base font-medium text-white pr-4" data-id="element-53">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} className="flex-shrink-0" data-id="element-54">
                  <ChevronDownIcon className="w-6 h-6 text-brand-primary" data-id="element-55" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-56">
                {openIndex === index && <motion.div id={`faq-answer-${index}`} initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-57">
                    <div className="px-5 pb-5" data-id="element-58">
                      <p className="text-white/80 leading-relaxed text-sm" data-id="element-59">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </motion.div>

        {/* Footer info */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-center border-t border-white/10 pt-12" data-id="element-60">
          <div className="mb-8" data-id="element-61">
            <h3 className="text-2xl font-light tracking-wider text-brand-primary italic mb-4" data-id="element-62">
              Dermaclinic
            </h3>
            <p className="text-white/60 text-sm mb-8 max-w-2xl mx-auto" data-id="element-63">
              Premium aesthetic treatments across the United States. Transform
              your body with the most advanced procedures available.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 max-w-5xl mx-auto" data-id="element-64">
            <div className="text-left bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-65">
              <div className="flex items-start gap-3" data-id="element-66">
                <MapPinIcon className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" data-id="element-67" />
                <div data-id="element-68">
                  <p className="text-white font-semibold mb-1" data-id="element-69">Miami, FL</p>
                  <p className="text-white/60 text-sm" data-id="element-70">
                    175 SW 7th St #2009, Miami, FL 33130
                  </p>
                </div>
              </div>
            </div>

            <div className="text-left bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-71">
              <div className="flex items-start gap-3" data-id="element-72">
                <MapPinIcon className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" data-id="element-73" />
                <div data-id="element-74">
                  <p className="text-white font-semibold mb-1" data-id="element-75">New York, NY</p>
                  <p className="text-white/60 text-sm" data-id="element-76">
                    116 Central Park South, New York, NY 10019
                  </p>
                </div>
              </div>
            </div>

            <div className="text-left bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-77">
              <div className="flex items-start gap-3" data-id="element-78">
                <MapPinIcon className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" data-id="element-79" />
                <div data-id="element-80">
                  <p className="text-white font-semibold mb-1" data-id="element-81">New York Lipo</p>
                  <p className="text-white/60 text-sm" data-id="element-82">
                    58 E 79th St 4R, New York, NY 10075
                  </p>
                </div>
              </div>
            </div>

            <div className="text-left bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-83">
              <div className="flex items-start gap-3" data-id="element-84">
                <MapPinIcon className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" data-id="element-85" />
                <div data-id="element-86">
                  <p className="text-white font-semibold mb-1" data-id="element-87">Houston, TX</p>
                  <p className="text-white/60 text-sm" data-id="element-88">
                    2702 Westheimer Rd, Houston, TX 77098
                  </p>
                </div>
              </div>
            </div>

            <div className="text-left bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-89">
              <div className="flex items-start gap-3" data-id="element-90">
                <MapPinIcon className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" data-id="element-91" />
                <div data-id="element-92">
                  <p className="text-white font-semibold mb-1" data-id="element-93">Las Vegas, NV</p>
                  <p className="text-white/60 text-sm" data-id="element-94">
                    2451 W Horizon Ridge Pkwy Suite 130, Henderson, NV 89052
                  </p>
                </div>
              </div>
            </div>

            <div className="text-left bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-95">
              <div className="flex items-start gap-3" data-id="element-96">
                <MapPinIcon className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" data-id="element-97" />
                <div data-id="element-98">
                  <p className="text-white font-semibold mb-1" data-id="element-99">Phoenix, AZ</p>
                  <p className="text-white/60 text-sm" data-id="element-100">
                    10505 N 69th St Suite 1100, Scottsdale, AZ 85253
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6" data-id="element-101">
            <p className="text-white/40 text-sm" data-id="element-102">
              © 2024 Dermaclinic. All rights reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}